import { useParams, Link } from 'react-router-dom';
import { articleDetails } from '@/mocks/articles';
import { homepageArticles } from '@/mocks/home';
import SEO from '@/components/feature/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? articleDetails[id] : undefined;

  if (!article) {
    return (
      <>
        <SEO title="文章未找到 - 爱风月" description="抱歉，您查找的文章不存在。" />
        <Navbar />
        <main className="pt-24 pb-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-6">
              <i className="ri-error-warning-line text-3xl" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您查找的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = article.relatedIds
    .map((rid) => homepageArticles.find((a) => a.id === rid))
    .filter(Boolean);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        datePublished: article.date,
        author: {
          '@type': 'Organization',
          name: '爱风月',
        },
        publisher: {
          '@type': 'Organization',
          name: '爱风月',
        },
        image: article.imageUrl,
        url: `https://aifengyue.com/article/${article.id}`,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://aifengyue.com/' },
          { '@type': 'ListItem', position: 2, name: article.category, item: `https://aifengyue.com/category/${article.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${article.title} - 爱风月`}
        description={article.excerpt}
        keywords={`爱风月,${article.category},风月文化,${article.title}`}
        canonicalUrl={`https://aifengyue.com/article/${article.id}`}
        ogType="article"
        schema={articleSchema}
      />

      <Navbar />

      <article className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <header className="mt-8 mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400">{article.readTime}</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-foreground-500 text-base leading-relaxed">
              {article.excerpt}
            </p>
          </header>

          <div className="aspect-[21/9] rounded-lg overflow-hidden mb-12">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:mt-10 prose-headings:mb-4
              prose-h2:text-2xl prose-h2:font-bold
              prose-h3:text-xl prose-h3:font-semibold
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5
              prose-blockquote:border-l-4 prose-blockquote:border-primary-400 prose-blockquote:bg-background-100 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-blockquote:not-italic
              prose-blockquote-p:text-foreground-600 prose-blockquote-p:text-sm
              prose-strong:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {relatedArticles.length > 0 && (
            <section className="mt-16 pt-12 border-t border-background-200/60">
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra!.id}
                    to={`/article/${ra!.id}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200/50 transition-all duration-300"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={ra!.imageUrl}
                        alt={ra!.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra!.title}
                      </h4>
                      <p className="text-xs text-foreground-400 mt-1">{ra!.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <Footer />
    </>
  );
}